import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { AppBreadcrumbItem } from "@shell/components/AppBreadcrumbs";

/**
 * Breadcrumb + content-width context for the Rusha shell.
 *
 * Pages publish their breadcrumbs (and optional fluid-content opt-in) to the
 * shell topbar via {@link useBreadcrumbs} / {@link useFluidContent}. The
 * `BreadcrumbProvider` lives inside `DashboardLayout` and feeds the current
 * values to the `Topbar` and the main content wrapper.
 *
 * The context ships with a no-op default so pages that render outside the
 * shell (e.g. in isolated unit tests) do not throw — breadcrumbs simply do
 * not appear when no provider is present. In the running app the provider is
 * always mounted by `DashboardLayout`.
 */
interface BreadcrumbContextValue {
  /** Current breadcrumb items shown in the topbar center area. */
  breadcrumbs: AppBreadcrumbItem[];
  /** Replace the current breadcrumb items. */
  setBreadcrumbs: (items: AppBreadcrumbItem[]) => void;
  /** Whether the page opted into full-width (fluid) content. */
  fluidContent: boolean;
  /** Toggle the fluid-content opt-in. */
  setFluidContent: (fluid: boolean) => void;
}

const noop = () => {};

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  breadcrumbs: [],
  setBreadcrumbs: noop,
  fluidContent: false,
  setFluidContent: noop,
});

interface BreadcrumbProviderProps {
  children: React.ReactNode;
}

/**
 * Provides breadcrumb and content-width state for the shell.
 */
export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({
  children,
}) => {
  const [breadcrumbs, setBreadcrumbsState] = useState<AppBreadcrumbItem[]>([]);
  const [fluidContent, setFluidContentState] = useState(false);

  const setBreadcrumbs = useCallback((items: AppBreadcrumbItem[]) => {
    setBreadcrumbsState(items);
  }, []);

  const setFluidContent = useCallback((fluid: boolean) => {
    setFluidContentState(fluid);
  }, []);

  const value = useMemo<BreadcrumbContextValue>(
    () => ({ breadcrumbs, setBreadcrumbs, fluidContent, setFluidContent }),
    [breadcrumbs, setBreadcrumbs, fluidContent, setFluidContent],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

/**
 * Access the breadcrumb/content-width context.
 *
 * Returns the no-op default when no provider is mounted, so callers stay safe
 * in isolated tests. Use {@link useBreadcrumbs} / {@link useFluidContent} for
 * the common page-level helpers instead of reading this directly.
 */
export function useBreadcrumbContext(): BreadcrumbContextValue {
  return useContext(BreadcrumbContext);
}

/**
 * Publish the page's breadcrumbs to the shell topbar.
 *
 * Pass the breadcrumb items; they appear in the topbar center area for the
 * lifetime of the page and are cleared on unmount. The items are compared by
 * their serialized form so identical breadcrumbs do not trigger redundant
 * context updates (the page can pass a fresh array each render safely).
 *
 * @example
 *   useBreadcrumbs([
 *     { label: "Organizations", to: "/organizations" },
 *     { label: "acme-corp", to: "/organizations/acme-corp" },
 *     { label: "Platform Products" },
 *   ]);
 */
export function useBreadcrumbs(items: AppBreadcrumbItem[]): void {
  const { setBreadcrumbs } = useBreadcrumbContext();
  const serialized = JSON.stringify(items);

  useEffect(() => {
    setBreadcrumbs(items);
    return () => {
      setBreadcrumbs([]);
    };
    // Re-run only when the breadcrumb shape changes; `items` is read from the
    // closure at the time the serialized key changes.
  }, [serialized, setBreadcrumbs]);
}

/**
 * Opt the page content area into full-width (fluid) layout, bypassing the
 * default {@link CONTENT_MAX_WIDTH} centering. Resets to centered on unmount.
 *
 * @param fluid `true` to disable the max-width (default).
 */
export function useFluidContent(fluid = true): void {
  const { setFluidContent } = useBreadcrumbContext();

  useEffect(() => {
    setFluidContent(fluid);
    return () => {
      setFluidContent(false);
    };
  }, [fluid, setFluidContent]);
}
